import React, { useState, useMemo, useEffect } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import styled from 'styled-components/macro';
import { theme } from 'styled-tools';
import { useMutation, useQuery } from '@apollo/client';
import Alert from 'components/Alerts';
import Button from 'components/Button';
import { UPDATE_MOBILE_PROMPT_REMINDER, UPDATE_USER } from 'routes/Profile/graphql';
import PhoneNumberEntryForm from './PhoneNumberEntryForm';
import { GET_INFO_TO_CONFIRM } from '../UserInfoConfirmationModal/graphql';
import { H2, H4 } from '../Typography';

export const ButtonWrapper = styled.div`
  display: flex;

  ${Button} {
    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }
`;

export const CancelButton = styled(Button)`
  background: ${theme('colors.white')};
  color: ${theme('link.color')};
`;

const ModalHeader = styled(H2)`
  margin-bottom: 1rem;
  text-align: center;
  color: ${theme('colors.primary')};
`;

const ModalSubHeader = styled(H4)`
  margin-bottom: 1rem;
  text-align: center;
  color: ${theme('colors.primary')};
`;

export const HAS_DISMISSED_PHONE_NUMBER_MODAL = 'has_dismissed_phone_number_modal';

function PhoneNumberFormModal() {
  const { data, loading, error, refetch } = useQuery(GET_INFO_TO_CONFIRM);
  const [alert, setAlert] = useState();
  const [hasDismissed, setHasDismissed] = useState(Boolean(localStorage.getItem(HAS_DISMISSED_PHONE_NUMBER_MODAL)));
  const [updateUser] = useMutation(UPDATE_USER);
  const [updateMobilePromptReminder] = useMutation(UPDATE_MOBILE_PROMPT_REMINDER);

  useEffect(() => {
    const updateDismissedState = () => {
      refetch()
        .catch(_ => {}) // This will throw an error, and it's expected (user is logged out, etc.)
        .finally(() => {
          setHasDismissed(false);
        });
    };

    window.addEventListener('user:login', updateDismissedState);
    window.addEventListener('user:logout', updateDismissedState);
    return () => {
      window.removeEventListener('user:login', updateDismissedState);
      window.removeEventListener('user:logout', updateDismissedState);
    };
  }, []);

  const isOlderThanAWeek =
    !data?.myUser?.mobilePromptReminder ||
    new Date(data.myUser.mobilePromptReminder) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  const isOpen = useMemo(() => {
    if (!!error || hasDismissed || loading) return false;
    return !!!data?.myUser?.mobileNumber && isOlderThanAWeek;
  }, [data, error, hasDismissed]);

  const onCancel = async (clickedRemindMeLater = false) => {
    // this is not redundant because clicking outside modal sends an event as the param, so we must check that the value is EXACTLY true, not just truthy
    if (clickedRemindMeLater === true) {
      try {
        await updateMobilePromptReminder();
      } catch (e) {
        setAlert({ type: 'error', message: e.message });
      }
    }

    localStorage.setItem(HAS_DISMISSED_PHONE_NUMBER_MODAL, 'true');
    setHasDismissed(true);
  };

  const handlePhoneSubmit = async (values, isMobileNumberVerified) => {
    if (!isMobileNumberVerified) {
      setAlert({ type: 'error', message: 'Mobile Number must be verified' });
      return;
    }

    // #TODO: not send the backend user data it already has. update resolver to allow update user given partial data, and it can handle the rest
    const variables = {
      id: data.myUser.id,
      firstName: data.myUser.firstName,
      lastName: data.myUser.lastName,
      email: data.myUser.email,
      occupations: data.myUser?.occupations?.map(occ => occ.id),
      state: data.myUser.memberAgencyState,
      memberAgency: data.myUser.memberAgency && data.myUser.memberAgency.id,
      isMasaMember: data.myUser.isMasaMember,
      recoveryEmail: data.myUser.recoveryEmail,
      mobileNumber: values.mobileNumber,
      smsOptIn: Boolean(values.smsOptIn),
      mobilePromptReminder: data.myUser.mobilePromptReminder,
      isMobileNumberVerified,
    };

    try {
      await updateUser({ variables });
      localStorage.setItem(HAS_DISMISSED_PHONE_NUMBER_MODAL, 'true');
      setHasDismissed(true);
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    }
  };

  // returning null here, not loading, because a loading state already exists on this page while data is being fetched
  if (loading || error || !data?.myUser) return null;

  return (
    <Modal isOpen={isOpen} centered toggle={onCancel}>
      <ModalBody>
        {alert && <Alert type={alert.type} alertText={alert.message} />}
        <ModalHeader>Add Your Mobile Number</ModalHeader>
        <ModalSubHeader>Simplify your life and stay informed</ModalSubHeader>
        <PhoneNumberEntryForm
          onSubmit={handlePhoneSubmit}
          onCancel={onCancel}
          loading={loading}
          setAlert={setAlert}
          mobilePromptReminder={data.myUser.mobilePromptReminder}
        />
      </ModalBody>
    </Modal>
  );
}

export default PhoneNumberFormModal;
